import { QuestionCircleOutlined } from '@ant-design/icons'
import { Tooltip } from 'antd'
import classNames from 'classnames'
import { useAtom } from 'jotai'
import { observer } from 'mobx-react'
import { useEffect, useState } from 'react'
import { GET_EXPECTED_SECONDS } from '~/graphqls/gqls'
import {
  getExpectedSeconds,
  getExpectedSecondsVariables,
  getExpectedSeconds_pictureGraph_aiArtworkTotalInfo_expectedSeconds,
} from '~/graphqls/types'
import { useImperativeQuery } from '~/hooks/useImperativeQuery'
import styles from '../AiForm.module.css'
import {
  genTidalAtom,
  modelCodeAtom,
  paintTimesAtom,
  selectModelAtom,
  totalInfoAtom,
} from './atom'
import { useType } from '../useType'

export const timeWithUint = (val: number) => {
  if (val <= 5 * 60) {
    return `${Math.ceil(val / 50)}分钟内`
  } else if (val <= 10 * 60) {
    return '10分钟内'
  } else if (val <= 30 * 60) {
    return '30分钟内'
  } else if (val <= 60 * 60) {
    return '1小时内'
  } else if (val <= 2 * 60 * 60) {
    return '2小时内'
  } else if (val <= 6 * 60 * 60) {
    return '6小时内'
  } else if (val <= 12 * 60 * 60) {
    return '12小时内'
  } else if (val <= 24 * 60 * 60) {
    return '24小时内'
  }
  return '24小时后'
}

export const GenerationComp = observer(() => {
  const [paintTimes] = useAtom(paintTimesAtom)
  const [totalInfo] = useAtom(totalInfoAtom)
  const [genTidal, setGenTidal] = useAtom(genTidalAtom)
  const [modelCode] = useAtom(modelCodeAtom)
  const [selectModel] = useAtom(selectModelAtom)
  const { isBigMJ } = useType()

  const getExpectedSecondsAction = useImperativeQuery<
    getExpectedSeconds,
    getExpectedSecondsVariables
  >(GET_EXPECTED_SECONDS)

  /** 预计时间 */
  const [expectedSeconds, setExpectedSeconds] =
    useState<getExpectedSeconds_pictureGraph_aiArtworkTotalInfo_expectedSeconds>(
      totalInfo?.expectedSeconds!
    )

  const onRefreshExpecteds = () => {
    getExpectedSecondsAction({
      modelCode: modelCode,
    })
      .then(res => {
        if (res.data.pictureGraph?.aiArtworkTotalInfo.expectedSeconds) {
          setExpectedSeconds(
            res.data.pictureGraph?.aiArtworkTotalInfo.expectedSeconds
          )
        }
      })
      .catch(() => {})
  }

  useEffect(() => {
    if (!!modelCode) {
      onRefreshExpecteds()
    }
  }, [modelCode])

  useEffect(() => {
    if (isBigMJ && genTidal) {
      setGenTidal(false)
    }
  }, [selectModel, isBigMJ])

  // 潮汐队列是否拥挤
  const isTidalQueueCrowd = expectedSeconds.tidalQueueOverloaded

  return (
    <div>
      <div
        className={classNames(
          'flex items-center justify-between cursor-pointer px-4 py-5 border-2 border-solid border-neutral-200 rounded-2xl duration-75 bg-neutral-200',
          {
            '!border-neutral-500': !genTidal,
            [styles.radialBg]: !genTidal,
          }
        )}
        onClick={() => setGenTidal(false)}
      >
        <div className="flex items-center">
          <span className="text-neutral-800 text-sm font-medium">
            {isBigMJ ? 'VIP快速生成' : '普通生成'}
          </span>
        </div>
        <div className="text-neutral-500 text-xs">
          预计排队：
          {timeWithUint(
            expectedSeconds?.freeExpectedSeconds +
              expectedSeconds.reduceTime * (paintTimes - 1)
          )}
        </div>
      </div>
      {!isBigMJ && (
        <div
          className={classNames(
            'mt-4  cursor-pointer px-4 py-5 border-2 border-solid border-neutral-200 rounded-2xl duration-75 bg-neutral-200',
            {
              '!border-neutral-500': genTidal && !isTidalQueueCrowd,
              [styles.radialBg]: genTidal && !isTidalQueueCrowd,
              '!border-[#f07474]': genTidal && isTidalQueueCrowd,
              [styles.radialBgRed]: genTidal && isTidalQueueCrowd,
            }
          )}
          onClick={() => setGenTidal(true)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <span className="text-neutral-800 text-sm font-medium">
                潮汐模式{' '}
                <Tooltip
                  placement="topLeft"
                  arrowPointAtCenter
                  title={
                    <>
                      普通用户优惠低至5折，会员用户免费无限创作，创作将按发起顺序消耗闲置算力依次生成，每个会员用户任意时刻在潮汐模式等待排队作画的任务数不超过20个。
                    </>
                  }
                >
                  <QuestionCircleOutlined />
                </Tooltip>
              </span>
            </div>

            {isTidalQueueCrowd && genTidal ? (
              <div className="text-[#F07474] text-xs">
                当前队列拥挤，需要等待较长时间
              </div>
            ) : (
              <div className="text-neutral-500 text-xs">
                优惠低至五折，会员免费生成
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
})
